/*
 * TrialDoors — primeiro contato do visitante anônimo (trial v2, chat vazio).
 *
 * Mensagem inicial LOCAL do Dr. Pera (template i18n — NÃO vem do LLM) com
 * dois botões-tarjeta:
 *   Porta A: cola um prontuário pronto → IA aprende o estilo do médico
 *   Porta B: começa direto com o modelo padrão
 *
 * Aparece SÓ pra anônimos com chat vazio (dia 0-1). Estética CRT do app.
 */
import { useState } from 'react';

export type TrialDoor = 'A' | 'B';

interface TrialDoorsProps {
  onSelect: (door: TrialDoor) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
}

/** Botão-tarjeta de uma porta — hover glow padrão do app */
function DoorCard({
  title,
  desc,
  onClick,
}: {
  title: string;
  desc: string;
  onClick: () => void;
}) {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
      style={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        padding: '16px 18px',
        background: 'var(--ink-soft)',
        border: `1px solid ${hovered ? 'rgba(124,124,248,0.85)' : 'rgba(124,124,248,0.30)'}`,
        boxShadow: hovered
          ? '0 0 14px rgba(124,124,248,0.45), 0 0 28px rgba(124,124,248,0.2)'
          : 'none',
        cursor: 'pointer',
        transition: 'border-color 180ms ease-out, box-shadow 180ms ease-out',
      }}
    >
      <div
        style={{
          fontFamily: 'var(--font-sans)',
          fontSize: '14px',
          fontWeight: 600,
          color: '#FFFFFF',
          marginBottom: '6px',
          lineHeight: 1.5,
        }}
      >
        ▶ {title}
      </div>
      <div
        style={{
          fontFamily: 'var(--font-sans)',
          fontSize: '13px',
          color: 'var(--ink-mute)',
          lineHeight: 1.6,
        }}
      >
        {desc}
      </div>
    </button>
  );
}

export function TrialDoors({ onSelect, t }: TrialDoorsProps) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '32px 20px',
        gap: '20px',
      }}
    >
      {/* Mascote — mesmo pattern do welcome existente */}
      <img
        src="/icons/icon-128.png"
        alt="Mascote Dr. Pera"
        width={64}
        height={64}
        style={{
          borderRadius: '50%',
          objectFit: 'cover',
          filter: 'drop-shadow(0 0 6px rgba(124, 124, 248, 0.55))',
        }}
      />

      {/* Mensagem inicial local do Dr. Pera */}
      <p
        style={{
          fontFamily: 'var(--font-sans)',
          fontSize: '14px',
          color: 'var(--ink-light)',
          lineHeight: 1.8,
          textAlign: 'center',
          maxWidth: '520px',
          whiteSpace: 'pre-wrap',
        }}
      >
        {t('trial.doors.intro')}
      </p>

      {/* As duas portas */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          width: '100%',
          maxWidth: '520px',
        }}
      >
        <DoorCard
          title={t('trial.doors.doorA.title')}
          desc={t('trial.doors.doorA.desc')}
          onClick={() => onSelect('A')}
        />
        <DoorCard
          title={t('trial.doors.doorB.title')}
          desc={t('trial.doors.doorB.desc')}
          onClick={() => onSelect('B')}
        />
      </div>

      {/* Fix 02/07/2026 (Dr. Lu) — porta de re-entrada pro assinante que
          abriu /ia numa máquina nova (ex.: PC do hospital): link discreto
          "Já sou assinante? Entrar" → /login (mesma rota do paywall). */}
      <a
        href="/login"
        aria-label={`${t('trial.login.question')} ${t('trial.login.cta')}`}
        style={{
          fontFamily: 'var(--font-sans)',
          fontSize: '13px',
          color: 'var(--ink-mute)',
          textDecoration: 'none',
          marginTop: '4px',
        }}
      >
        {t('trial.login.question')}{' '}
        <span
          style={{
            color: 'var(--ink-light)',
            fontWeight: 600,
            textDecoration: 'underline',
            textUnderlineOffset: '3px',
          }}
        >
          {t('trial.login.cta')}
        </span>
      </a>
    </div>
  );
}
